import { Fragment, useState, type ReactNode } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';

export function ConfigurationGate({ children }: { readonly children: ReactNode }) {
  const permissions = useExpensesPermissions();
  const context = useActiveContextOptional();
  const { t } = useTranslation('expenses');
  if (permissions.status === 'loading') return <p role="status">{t('configuration.loading')}</p>;
  if (!holds(permissions, EXPENSES_MANAGE)) return <Navigate to="/expenses" replace />;
  return (
    <Fragment key={`${context?.active?.tenantId}:${context?.active?.organizationId}`}>
      {children}
    </Fragment>
  );
}
export function ConfigSelect({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { value: string; label: string }[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 text-sm">
      <span>{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label} className="min-h-11 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
/**
 * Where the focus goes back when a panel closes: what opened it, if still on the page; else the
 * marked return point or «Nuovo …». The page may render a marker twice, once in the head and once
 * as the phone's floating button (S9), with one of them `display: none`; focusing a hidden one does
 * nothing and loses the focus, so the first one the browser shows wins (#952, main's e2e at 390 px).
 * Where `checkVisibility` is missing, every candidate counts as shown.
 */
export function returnFocusTarget(returnTo: HTMLElement | null): HTMLElement | null {
  if (returnTo?.isConnected) return returnTo;
  const candidates = [
    ...document.querySelectorAll<HTMLElement>('[data-purpose-return], [data-purpose-create]'),
  ];
  const shown = (element: HTMLElement): boolean =>
    typeof element.checkVisibility === 'function' ? element.checkVisibility() : true;
  return candidates.find(shown) ?? null;
}

export function ConfigPanel({
  title,
  description,
  onClose,
  children,
  footer,
}: {
  readonly title: string;
  readonly description: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer: ReactNode;
}) {
  const [returnTo] = useState(() =>
    document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null,
  );
  return (
    <SidePanel
      open
      title={title}
      description={description}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        returnFocusTarget(returnTo)?.focus();
      }}
      footer={footer}
    >
      <div className="space-y-5">{children}</div>
    </SidePanel>
  );
}

export function ConfigProblem({
  code,
  retry,
}: {
  readonly code: string;
  readonly retry?: () => void;
}) {
  const { t } = useTranslation('expenses');
  const known = [
    'PURPOSE_NOT_FOUND',
    'PURPOSE_CODE_EXISTS',
    'CONFIGURATION_CHANGED',
    'PURPOSE_NOTE_TYPE_REQUIRED',
    'NOTE_TYPE_CATEGORY_REQUIRED',
    'CATEGORY_UNKNOWN',
    'CATEGORY_NOT_ALLOWED_FOR_NOTE_TYPE',
    'FLAT_RATE_CATEGORY_REQUIRED',
    'FORBIDDEN',
    'REPORT_FORBIDDEN',
  ] as const;
  return (
    <div
      role="alert"
      className="border-destructive/30 bg-destructive/5 space-y-2 rounded-lg border p-3 text-sm"
    >
      <p>{t(`configuration.problems.${known.find((item) => item === code) ?? 'OTHER'}`)}</p>
      {retry ? (
        <Button variant="outline" className="min-h-11" onClick={retry}>
          {t('configuration.retry')}
        </Button>
      ) : null}
    </div>
  );
}
export function ConfigPages({
  page,
  pageSize,
  total,
  onPage,
}: {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
}) {
  const { t } = useTranslation('expenses');
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const visible = Array.from(
    new Set([1, Math.max(1, page - 1), page, Math.min(pages, page + 1), pages]),
  ).filter((p) => p <= pages);
  return (
    <nav aria-label={t('configuration.pagination')} className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        className="min-h-11"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        {t('configuration.previous')}
      </Button>
      {visible.map((p) => (
        <Button
          key={p}
          variant={p === page ? 'default' : 'ghost'}
          className="min-h-11 min-w-11"
          aria-current={p === page ? 'page' : undefined}
          aria-label={t('configuration.page', { page: p })}
          onClick={() => onPage(p)}
        >
          {p}
        </Button>
      ))}
      <Button
        variant="outline"
        className="min-h-11"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        {t('configuration.next')}
      </Button>
    </nav>
  );
}
